(() => {
  if (window.FoxThemeProductCardHoverVideoInitialized) return;
  window.FoxThemeProductCardHoverVideoInitialized = true;

  const selectors = {
    trigger: '[data-product-card-hover-video-trigger]',
    video: '[data-product-card-hover-video]',
  };

  const desktopQuery = window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function canPlayHoverVideo() {
    return desktopQuery.matches && !reducedMotionQuery.matches;
  }

  function getTriggerFromEvent(event) {
    if (!(event.target instanceof Element)) return null;
    return event.target.closest(selectors.trigger);
  }

  function isSameTriggerTransition(trigger, relatedTarget) {
    return relatedTarget instanceof Element && trigger.contains(relatedTarget);
  }

  function isHostedVideo(videoElement) {
    return videoElement && !videoElement.hasAttribute('source');
  }

  function syncPlayingState(videoElement, isPlaying) {
    const trigger = videoElement?.closest(selectors.trigger);

    if (isPlaying) {
      videoElement?.setAttribute('playing', '');
      if (trigger?.classList.contains('is-hover-video-active')) {
        trigger.classList.add('is-hover-video-playing');
      }
    } else {
      videoElement?.removeAttribute('playing');
      trigger?.classList.remove('is-hover-video-playing');
    }
  }

  function ensureHostedVideo(videoElement) {
    let nativeVideo = videoElement.querySelector('video');
    if (nativeVideo) return nativeVideo;

    const template = videoElement.querySelector('template');
    const templateVideo = template?.content?.firstElementChild;
    if (!(templateVideo instanceof HTMLVideoElement)) return null;

    nativeVideo = templateVideo.cloneNode(true);
    nativeVideo.muted = true;
    nativeVideo.loop = true;
    nativeVideo.playsInline = true;
    nativeVideo.preload = 'auto';
    nativeVideo.setAttribute('aria-hidden', 'true');
    nativeVideo.setAttribute('tabindex', '-1');

    nativeVideo.addEventListener('play', () => syncPlayingState(videoElement, true));
    nativeVideo.addEventListener('playing', () => syncPlayingState(videoElement, true));
    nativeVideo.addEventListener('pause', () => {
      if (!nativeVideo.seeking && nativeVideo.paused) {
        syncPlayingState(videoElement, false);
      }
    });
    nativeVideo.addEventListener('ended', () => syncPlayingState(videoElement, false));

    template.remove();
    videoElement.appendChild(nativeVideo);
    videoElement.setAttribute('loaded', '');

    return nativeVideo;
  }

  function resetHostedVideo(nativeVideo) {
    if (!nativeVideo || nativeVideo.readyState === 0) return;

    try {
      nativeVideo.currentTime = 0;
    } catch (error) {
      // Some browsers reject currentTime changes while media metadata is still settling.
    }
  }

  function pauseTrigger(trigger) {
    const videoElement = trigger?.querySelector(selectors.video);
    trigger?.classList.remove('is-hover-video-active', 'is-hover-video-playing');

    if (isHostedVideo(videoElement)) {
      const nativeVideo = videoElement.querySelector('video');
      if (nativeVideo) {
        nativeVideo.pause();
        resetHostedVideo(nativeVideo);
      }
      syncPlayingState(videoElement, false);
      return;
    }

    if (videoElement && typeof videoElement.pause === 'function') {
      videoElement.pause();
    }
    syncPlayingState(videoElement, false);
  }

  function pauseAllHoverVideos(exceptTrigger = null) {
    document
      .querySelectorAll(`${selectors.trigger}.is-hover-video-active, ${selectors.trigger}.is-hover-video-playing`)
      .forEach((trigger) => {
        if (trigger !== exceptTrigger) {
          pauseTrigger(trigger);
        }
      });
  }

  function handlePointerOver(event) {
    if (!canPlayHoverVideo()) return;

    const trigger = getTriggerFromEvent(event);
    if (!trigger || isSameTriggerTransition(trigger, event.relatedTarget)) return;

    const videoElement = trigger.querySelector(selectors.video);
    if (!videoElement) return;

    pauseAllHoverVideos(trigger);
    trigger.classList.add('is-hover-video-active');

    if (isHostedVideo(videoElement)) {
      const nativeVideo = ensureHostedVideo(videoElement);
      if (!nativeVideo) return;

      nativeVideo.muted = true;
      nativeVideo.preload = 'auto';
      resetHostedVideo(nativeVideo);
      syncPlayingState(videoElement, true);

      const playPromise = nativeVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          if (trigger.classList.contains('is-hover-video-active')) {
            pauseTrigger(trigger);
          }
        });
      }
      return;
    }

    if (typeof videoElement.play === 'function') {
      videoElement.play();
    }
  }

  function handlePointerOut(event) {
    const trigger = getTriggerFromEvent(event);
    if (!trigger || isSameTriggerTransition(trigger, event.relatedTarget)) return;

    pauseTrigger(trigger);
  }

  document.addEventListener('pointerover', handlePointerOver);
  document.addEventListener('pointerout', handlePointerOut);

  document.addEventListener('video:play', (event) => {
    const videoElement = event.target instanceof Element ? event.target.closest(selectors.video) : null;
    const trigger = videoElement?.closest(selectors.trigger);

    if (!trigger || !trigger.classList.contains('is-hover-video-active')) {
      if (videoElement && typeof videoElement.pause === 'function') {
        videoElement.pause();
      }
      return;
    }
    trigger.classList.add('is-hover-video-playing');
  });

  document.addEventListener('video:pause', (event) => {
    const videoElement = event.target instanceof Element ? event.target.closest(selectors.video) : null;
    videoElement?.closest(selectors.trigger)?.classList.remove('is-hover-video-playing');
  });

  function addMediaChangeListener(mediaQuery, callback) {
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', callback);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(callback);
    }
  }

  addMediaChangeListener(desktopQuery, () => {
    if (!canPlayHoverVideo()) {
      pauseAllHoverVideos();
    }
  });

  addMediaChangeListener(reducedMotionQuery, () => {
    if (!canPlayHoverVideo()) {
      pauseAllHoverVideos();
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      pauseAllHoverVideos();
    }
  });
})();
